import axios from 'axios'
import Cookies from 'js-cookie'
import * as Sentry from '@sentry/react'
import Encryption from '../utils/encryption'
import Sanitizer from '../utils/sanitizer'
import {
  handleSessionExpired,
  clearExpiredCookies,
  hasAuthTokens,
  getTenantUrlFromPath,
} from '../utils/sessionManager'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'
import { getCurrentPageContext } from '../utils/sentryConfig'

/**
 * Secure API Service
 * Configured axios instance with security features
 */

// Get API base URL from runtime config or environment variable
const getApiBaseUrl = () => {
  // Check if runtime config is available (set via window object)
  if (window._env_ && window._env_.REACT_APP_API_BASE_URL) {
    const runtimeUrl = window._env_.REACT_APP_API_BASE_URL
    if (runtimeUrl && !runtimeUrl.includes('__REACT_APP_')) {
      return runtimeUrl
    }
  }

  // Fallback to build-time environment variable
  if (process.env.REACT_APP_API_BASE_URL) {
    const buildUrl = process.env.REACT_APP_API_BASE_URL
    if (buildUrl && !buildUrl.includes('__REACT_APP_')) {
      return buildUrl
    }
  }

  // If no valid URL configured, use current domain as fallback
  return window.location.origin
}

// Create axios instance
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

const getRequestPath = (config = {}) => {
  try {
    const baseUrl = config.baseURL || window.location.origin
    return new URL(config.url || '', baseUrl).pathname
  } catch {
    return config.url || 'unknown'
  }
}

const getRequestId = (config = {}) =>
  config.headers?.['X-Request-ID'] || config.headers?.['x-request-id']

const getResponseRequestId = (response = {}) =>
  response.headers?.['x-request-id'] ||
  response.headers?.['X-Request-ID'] ||
  getRequestId(response.config)

const getErrorRequestId = (error = {}) =>
  getResponseRequestId(error.response) || getRequestId(error.config)

const addApiBreadcrumb = (config = {}, level = 'info', data = {}) => {
  const pageContext = getCurrentPageContext()

  Sentry.addBreadcrumb({
    category: 'api',
    type: 'http',
    level,
    message: `${(config.method || 'GET').toUpperCase()} ${getRequestPath(config)}`,
    data: {
      method: config.method,
      endpoint: getRequestPath(config),
      requestId: getRequestId(config),
      tenantUrl: pageContext.tenantUrl,
      currentPath: pageContext.currentPath,
      ...data,
    },
  })
}

const shouldReportApiError = (error) => {
  const isCanceled =
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.message === 'canceled' ||
    error.message === 'Request aborted'

  return !isCanceled
}

// Rate limiting tracker
const rateLimitTracker = {
  requests: [],
  maxRequests: 1000,
  timeWindow: 60000, // 1 minute
}

// Shared refresh promise — ensures only one refresh request is in-flight at a time.
// Both the response interceptor and ClientContext.refreshToken defer to this.
let _refreshPromise = null

const SAFE_RETRY_METHODS = ['get', 'head', 'options']
const RETRYABLE_STATUS_CODES = [408, 429, 502, 503, 504]
const DEFAULT_MAX_RETRIES = 2

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const isRefreshRequest = (config = {}) =>
  config?._isRefreshRequest || (config?.url || '').includes('/api/kc/refresh')

const isNetworkOrTimeoutError = (error) =>
  !error.response ||
  error.code === 'ERR_NETWORK' ||
  error.code === 'ECONNABORTED' ||
  /timeout/i.test(error.message || '')

const getRetryAfterDelay = (retryAfter) => {
  if (!retryAfter) return null

  const retryAfterSeconds = Number(retryAfter)
  if (!Number.isNaN(retryAfterSeconds)) {
    return retryAfterSeconds * 1000
  }

  const retryAfterDate = Date.parse(retryAfter)
  if (!Number.isNaN(retryAfterDate)) {
    return Math.max(retryAfterDate - Date.now(), 0)
  }

  return null
}

const getRetryDelay = (error, retryCount) => {
  const retryAfterDelay = getRetryAfterDelay(error.response?.headers?.['retry-after'])
  if (retryAfterDelay !== null) return retryAfterDelay

  const baseDelay = 500 * Math.pow(2, retryCount - 1)
  const jitter = Math.floor(Math.random() * 150)
  return Math.min(baseDelay + jitter, 3000)
}

const canRetryRequest = (error) => {
  const config = error.config
  if (!config || config._skipRetry || isRefreshRequest(config)) return false

  const retryCount = config._retryCount || 0
  const maxRetries = config._maxRetries ?? DEFAULT_MAX_RETRIES
  if (retryCount >= maxRetries) return false

  const method = (config.method || 'get').toLowerCase()
  const isSafeMethod = SAFE_RETRY_METHODS.includes(method)
  const optedInUnsafeRetry = config._retrySafe === true
  if (!isSafeMethod && !optedInUnsafeRetry) return false

  const status = error.response?.status
  if (status && !RETRYABLE_STATUS_CODES.includes(status)) return false

  return isNetworkOrTimeoutError(error) || RETRYABLE_STATUS_CODES.includes(status)
}

const retryRequest = async (error) => {
  const config = error.config
  config._retryCount = (config._retryCount || 0) + 1

  const delay = getRetryDelay(error, config._retryCount)
  addApiBreadcrumb(config, 'warning', {
    retryCount: config._retryCount,
    retryDelayMs: delay,
    status: error.response?.status || 0,
    code: error.code,
  })

  await wait(delay)
  return apiClient(config)
}

/**
 * Attempt a token refresh, with a single retry on transient network errors.
 * Returns the response data on success, or throws on failure.
 * Concurrent callers share the same in-flight promise.
 */
const refreshAccessToken = () => {
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async () => {
    const attemptRefresh = () =>
      apiClient.post('/api/kc/refresh', {}, { _isRefreshRequest: true })

    try {
      const response = await attemptRefresh()
      return response.data
    } catch (err) {
      // Retry once on transient network errors (no response from server)
      const isNetworkError = !err.response && err.message !== 'canceled'
      if (isNetworkError) {
        await new Promise((r) => setTimeout(r, 1000))
        const response = await attemptRefresh()
        return response.data
      }
      throw err
    }
  })().finally(() => {
    _refreshPromise = null
  })

  return _refreshPromise
}

/**
 * Custom error class for client-side rate limiting.
 * This is an expected condition (not a bug), so it should NOT be reported to Sentry.
 */
class RateLimitError extends Error {
  constructor(message = 'Rate limit exceeded. Please try again later.') {
    super(message)
    this.name = 'RateLimitError'
    this.isRateLimit = true
  }
}

/**
 * Check rate limit
 */
const checkRateLimit = () => {
  const now = Date.now()
  rateLimitTracker.requests = rateLimitTracker.requests.filter(
    (time) => now - time < rateLimitTracker.timeWindow,
  )

  if (rateLimitTracker.requests.length >= rateLimitTracker.maxRequests) {
    throw new RateLimitError()
  }

  rateLimitTracker.requests.push(now)
}

/**
 * Sanitize tenant response while preserving HTML content
 */
const sanitizeTenantResponse = (data) => {
  if (!data || typeof data !== 'object') return data

  // Rich-content containers may arrive as strings or parsed objects.
  // Preserve them as-is here and sanitize at render sites where needed.
  const preservedFields = [
    'tenantfaq',
    'cookiepolicy',
    'tenanttermsandconditions',
    'privacypolicy',
    'aboutus',
    'tenantservicepolicy',
    'tenantlanguagecontent',
    'osakalanguagecontent',
    'tenantcontentconfig',
  ]

  // Create a copy of the data
  const sanitized = Array.isArray(data) ? [...data] : { ...data }

  // Recursively sanitize the object
  for (const key in sanitized) {
    if (sanitized.hasOwnProperty(key)) {
      const value = sanitized[key]

      if (preservedFields.includes(key)) {
        // Preserve rich-content payloads regardless of whether they are
        // serialized JSON strings or already-parsed objects.
        sanitized[key] = value
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeTenantResponse(value)
      } else if (typeof value === 'string') {
        // Strip HTML from other string fields
        sanitized[key] = Sanitizer.sanitizeText(value)
      } else {
        sanitized[key] = value
      }
    }
  }

  return sanitized
}

/**
 * Sanitize offer response while preserving HTML content in specific fields
 */
const sanitizeOfferResponse = (data) => {
  if (!data || typeof data !== 'object') return data

  // Fields that contain HTML content and should be preserved
  const htmlFields = [
    'description',
    'termsAndConditions',
    'tncText',
    'participatingMerchants',
    'eligibleCards',
  ]

  // Create a copy of the data
  const sanitized = Array.isArray(data) ? [...data] : { ...data }

  // Recursively sanitize the object
  for (const key in sanitized) {
    if (sanitized.hasOwnProperty(key)) {
      const value = sanitized[key]

      if (htmlFields.includes(key) && typeof value === 'string') {
        // Preserve HTML for these fields - don't sanitize
        sanitized[key] = value
      } else if (Array.isArray(value)) {
        // Recursively sanitize arrays (e.g., offers array)
        sanitized[key] = value.map((item) => sanitizeOfferResponse(item))
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeOfferResponse(value)
      } else if (typeof value === 'string') {
        // Strip HTML from other string fields
        sanitized[key] = Sanitizer.sanitizeText(value)
      } else {
        sanitized[key] = value
      }
    }
  }

  return sanitized
}

/**
 * Request Interceptor
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Check rate limit
      checkRateLimit()

      // Add CSRF token
      const csrfToken = Cookies.get('XSRF-TOKEN')
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken
      }

      // Add API key
      if (process.env.REACT_APP_API_KEY) {
        config.headers['X-API-Key'] = process.env.REACT_APP_API_KEY
      }

      // Add request ID for tracking
      config.headers['X-Request-ID'] = Encryption.generateToken(16)

      // Add timestamp
      config.headers['X-Timestamp'] = Date.now().toString()

      // Sanitize request data
      if (config.data) {
        config.data = Sanitizer.sanitizeObject(config.data)
      }

      // Sanitize URL parameters
      if (config.params) {
        config.params = Sanitizer.sanitizeObject(config.params)
      }

      addApiBreadcrumb(config, 'info')

      return config
    } catch (error) {
      return Promise.reject(error)
    }
  },
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * Response Interceptor
 */
apiClient.interceptors.response.use(
  (response) => {
    // Check if this is a tenant API response that contains HTML content
    const isTenantResponse = response.config?.url?.includes('/api/tenants/')
    // Check if this is an offer API response that contains HTML content
    const isOfferResponse = response.config?.url?.includes('/api/offers/')

    // Sanitize response data
    if (response.data) {
      if (isTenantResponse) {
        // For tenant responses, preserve HTML in specific fields
        response.data = sanitizeTenantResponse(response.data)
      } else if (isOfferResponse) {
        // For offer responses, preserve HTML in description, termsAndConditions, participatingMerchants, eligibleCards fields
        response.data = sanitizeOfferResponse(response.data)
      } else {
        // For other responses, use default sanitization
        response.data = Sanitizer.sanitizeObject(response.data)
      }
    }

    // Update CSRF token if provided
    const newCsrfToken = response.headers['x-csrf-token']
    if (newCsrfToken) {
      Cookies.set('XSRF-TOKEN', newCsrfToken, {
        secure: process.env.REACT_APP_ENV === 'production',
        sameSite: 'strict',
      })
    }

    addApiBreadcrumb(response.config, 'info', {
      status: response.status,
      responseRequestId: getResponseRequestId(response),
    })

    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Never re-intercept refresh requests — break the loop immediately
    if (
      originalRequest?._isRefreshRequest ||
      (originalRequest?.url || '').includes('/api/kc/refresh')
    ) {
      return Promise.reject(error)
    }

    // Tenant mismatch — don't attempt refresh, token is valid for the original tenant
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'TENANT_MISMATCH'
    ) {
      return Promise.reject(error)
    }

    // Handle 401 Unauthorized / 403 Forbidden - Token expired or invalid
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      const responseData = error.response?.data
      const shouldAttemptRefresh =
        responseData?.needsRefresh || responseData?.code === 'TOKEN_EXPIRED'
      const wasAuthenticated = hasAuthTokens() // Checks ow-c and any visible auth cookies

      if (shouldAttemptRefresh || wasAuthenticated) {
        try {
          await refreshAccessToken()
          return apiClient(originalRequest)
        } catch (refreshError) {
          const tenantUrl = getTenantUrlFromPath()
          handleSessionExpired(tenantUrl)
          return Promise.reject(refreshError)
        }
      }

      // User was anonymous, just clear any stale cookies
      clearExpiredCookies()
      return Promise.reject(error)
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      logger.error('Access forbidden:', sanitizeLogMessage(error.response.data))
    }

    // Handle 429 Too Many Requests
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after']
      logger.error(
        sanitizeLogMessage(`Rate limited. Retry after ${retryAfter} seconds`),
      )
    }

    if (canRetryRequest(error)) {
      return retryRequest(error)
    }

    // Handle network errors
    if (!error.response) {
      logger.error('Network error:', sanitizeLogMessage(error.message))
      logger.error(
        'Request config:',
        sanitizeLogMessage({
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
        }),
      )
    }

    addApiBreadcrumb(originalRequest, 'error', {
      status: error.response?.status || 0,
      statusText: error.response?.statusText,
      code: error.code,
      retried: Boolean(originalRequest?._retry),
      retryCount: originalRequest?._retryCount || 0,
      responseRequestId: getErrorRequestId(error),
    })

    // Log all errors for debugging
    logger.error(
      'API Error:',
      sanitizeLogMessage({
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data,
      }),
    )

    return Promise.reject(error)
  },
)

/**
 * API Service Methods
 */
const apiService = {
  /**
   * GET request
   */
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config)
      return response.data
    } catch (error) {
      throw apiService.handleError(error)
    }
  },

  /**
   * POST request
   */
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config)
      return response.data
    } catch (error) {
      throw apiService.handleError(error)
    }
  },

  /**
   * PUT request
   */
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config)
      return response.data
    } catch (error) {
      throw apiService.handleError(error)
    }
  },

  /**
   * PATCH request
   */
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config)
      return response.data
    } catch (error) {
      throw apiService.handleError(error)
    }
  },

  /**
   * DELETE request
   */
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config)
      return response.data
    } catch (error) {
      throw apiService.handleError(error)
    }
  },

  /**
   * Upload file
   */
  upload: async (url, file, onProgress) => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            )
            onProgress(percentCompleted)
          }
        },
      })

      return response.data
    } catch (error) {
      throw apiService.handleError(error)
    }
  },

  /**
   * Handle API errors
   */
  handleError: (error) => {
    const status = error.response?.status

    if (shouldReportApiError(error)) {
      const endpoint = getRequestPath(error.config)
      const pageContext = getCurrentPageContext()

      Sentry.captureException(error, {
        tags: {
          type: 'api_error',
          status: status || 0,
          endpoint,
          tenant_url: pageContext.tenantUrl,
          current_path: pageContext.currentPath,
          api_request_id: getErrorRequestId(error) || 'unknown',
        },
        fingerprint: [
          'api_error',
          error.config?.method || 'unknown',
          endpoint,
          String(status || error.code || 'network'),
        ],
        extra: {
          method: error.config?.method,
          requestId: getRequestId(error.config),
          responseRequestId: getErrorRequestId(error),
          pageUrl: pageContext.pageUrl,
          errorCode: error.code,
          responseReason: error.response?.data?.reason,
          responseCode: error.response?.data?.code,
        },
      })
    }

    if (error.response) {
      // Server responded with error
      const message = error.response.data?.message || 'An error occurred'
      return {
        message,
        status: error.response.status,
        data: error.response.data,
        isRateLimit:
          error.isRateLimit || error.name === 'RateLimitError' || status === 429,
      }
    } else if (error.request) {
      // Request made but no response
      return {
        message: 'No response from server. Please check your connection.',
        status: 0,
      }
    } else {
      // Error in request setup (includes client-side rate limit)
      return {
        message: error.message || 'An error occurred',
        status: 0,
        isRateLimit:
          error.isRateLimit || error.name === 'RateLimitError' || status === 429,
      }
    }
  },
}

export default apiService
export { apiClient, refreshAccessToken }
